import { AddonsLlmKeyResponseModel } from '@newfold/huapi-js/src/index.schemas';

import { formatDisplayDate } from '~/utils/date/helpers';

import {
  aiAppDisplayNameMap,
  AiBrand,
  aiCreditArticleUrlMap,
  API_KEY_NAME_MAX_LENGTH,
  API_KEY_NAME_MIN_LENGTH,
  DEFAULT_AI_BRAND,
  installedAIApps,
  SUPPORTED_AI_BRANDS,
} from './constants';
import {
  AiApiKey,
  ApiKeyNameValidationError,
  InstalledAppHowToItemParams,
} from './types';

/** Letters, numbers, hyphens, and underscores; must start and end with a letter or number. */
export const API_KEY_NAME_PATTERN = /^[a-zA-Z0-9]([a-zA-Z0-9_-]*[a-zA-Z0-9])?$/;

export const mapLlmApiKey = (key: AddonsLlmKeyResponseModel): AiApiKey => ({
  id: key.key_name ?? '',
  name: key.key_name ?? '',
  created: formatDisplayDate(key.created_at),
  lastUsed: formatDisplayDate(key.last_used_at),
});

export const validateApiKeyName = (
  name: string,
): ApiKeyNameValidationError | null => {
  const trimmed = name.trim();
  if (!trimmed) return 'required';
  if (trimmed.length < API_KEY_NAME_MIN_LENGTH) return 'minLength';
  if (trimmed.length > API_KEY_NAME_MAX_LENGTH) return 'maxLength';
  if (!API_KEY_NAME_PATTERN.test(trimmed)) return 'invalidFormat';
  return null;
};

export const isValidApiKeyName = (name: string) =>
  validateApiKeyName(name) === null;

export const resolveAiBrand = (brandFromJWT: string): AiBrand => {
  const normalised = brandFromJWT?.toLowerCase() as AiBrand;
  return SUPPORTED_AI_BRANDS.includes(normalised)
    ? normalised
    : DEFAULT_AI_BRAND;
};

/** Strips hyphens so API values like 'claude-code' match constant keys like 'claudecode'. */
export const normalizeAppId = (app: string): string => app.replace(/-/g, '');

const buildHowToItem = ({
  title,
  description,
  articleUrl,
  displayVideoArticleCard = false,
  aiImageApp,
}: {
  title: string;
  description: string;
  articleUrl: string;
  displayVideoArticleCard?: boolean;
  aiImageApp?: string;
}) => ({
  type: 'howto' as const,
  title,
  description,
  articleUrl,
  displayVideoArticleCard,
  ...(aiImageApp && { aiImageApp }),
});

export const getInstalledAppHowToItem = ({
  installedApp,
  aiBrand,
  brandInfo,
}: InstalledAppHowToItemParams) => {
  const normalizedApp = normalizeAppId(installedApp);
  if (!normalizedApp || !installedAIApps.includes(normalizedApp)) {
    return {
      appHowToItems: [],
      creditPricingItems: [
        buildHowToItem({
          title: `aiTool.${aiBrand}.titleCreditPricing`,
          description: 'aiTool.descCreditPricing',
          articleUrl:
            (brandInfo.aiModalCreditPricingArticleUrl as string) ?? '',
        }),
      ],
    };
  }
  const articleUrl =
    (brandInfo[aiCreditArticleUrlMap[normalizedApp]] as string) || '';
  if (!articleUrl) {
    return { appHowToItems: [], creditPricingItems: [] };
  }
  return {
    appHowToItems: [
      buildHowToItem({
        title: `aiTool.${aiBrand}.title`,
        description: 'aiTool.descForAiApp',
        articleUrl,
        aiImageApp: aiAppDisplayNameMap[normalizedApp] ?? normalizedApp,
      }),
    ],
    creditPricingItems: [],
  };
};
